import type { Editor } from "@tiptap/core";
import type { Cue, Id, NoteColumnDef, Scene, ScriptColumn } from "@supermanus/document-model";
import { getCueNote, getSceneNote, layoutDualColumnSpans } from "@supermanus/document-model";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { DocumentHead } from "./DocumentHead";
import { DurationField } from "./DurationField";
import { CueHtmlEditor, type CellNavDir } from "./CueHtmlEditor";
import { isCueContentEmpty, looksLikeCueHtml, sanitizeCueHtml } from "./cueRichText";
import type { RowRuleMode, ScriptColumnOrder } from "./editorSettings";
import { cueShowsRowRule, isAudioOnlyColumnOrder } from "./editorSettings";
import { InlineEditable } from "./InlineEditable";
import { isEmptyForAudioOnlyPrint } from "./manuscriptExport";
import { SceneHeadingLine } from "./SceneHeadingLine";
import { sceneHeaderBg } from "./SceneHeaderColorMenu";

export interface SourcesViewProps {
  scene: Scene;
  cues: Cue[];
  readOnly?: boolean;
  productionTitle: string;
  seasonTitle?: string | null;
  episodeTitle: string;
  defaultSceneHeaderBg: string;
  showColumnRule?: boolean;
  rowRuleMode?: RowRuleMode;
  rowRuleHitInGutter?: boolean;
  onTogglePrintBorderBelow?: (cueId: Id) => void;
  columnOrder?: ScriptColumnOrder;
  noteColumns: NoteColumnDef[];
  activeNoteColumnId: Id | null;
  onSelectNoteColumn: (columnId: Id) => void;
  onRenameNoteColumn: (columnId: Id, title: string) => void;
  onAddNoteColumn: () => void;
  onRemoveNoteColumn: (columnId: Id) => void;
  onCueNoteChange: (cueId: Id, columnId: Id, text: string) => void;
  onSceneNoteChange?: (columnId: Id, text: string) => void;
  onCueChange?: (cueId: Id, column: ScriptColumn, content: string) => void;
  /** Double Return in unlocked manuscript cells — same as manus view. */
  onNewPair?: (
    afterCueId: Id,
    column: ScriptColumn,
    flushedContent: string,
    place?: "after" | "before",
  ) => Id | null;
  newRowOnDoubleReturn?: boolean;
  onCueFocus?: (cueId: Id, column: ScriptColumn) => void;
  onCueBlur?: (cueId: Id, column: ScriptColumn) => void;
  onActiveEditorChange?: (editor: Editor | null) => void;
  contentResetKey?: number | string;
  onSceneMetaChange?: (
    patch: Partial<
      Pick<
        Scene,
        | "title"
        | "number"
        | "heading"
        | "headingKind"
        | "headingLocation"
        | "headingTime"
        | "headingNote"
        | "durationSeconds"
      >
    >,
  ) => void;
  showHours?: boolean;
  showFrames?: boolean;
  fps?: number;
  /** First click picks the note; second click opens it for typing. Shift-click always picks. */
  clickSelectsCell?: boolean;
  onSceneHeaderContextMenu?: (e: MouseEvent) => void;
  onCopyNoteColumnLink?: (columnId: Id) => void;
  /** Jump focus to a note cell (e.g. from find). */
  focusNoteCueId?: Id | null;
  focusNoteSelectQuery?: string;
  focusNoteSelectOccurrence?: number;
  focusNoteNonce?: number;
  onFocusNoteCueHandled?: () => void;
}

/** Turn plain text into React nodes with auto-linked http(s) and www. URLs.
 * Newlines are preserved as `<br />`.
 */
export function linkifyText(text: string): ReactNode[] {
  const lines = text.split("\n");
  const nodes: React.ReactNode[] = [];
  let key = 0;

  for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
    if (lineIdx > 0) {
      nodes.push(<br key={`br-${key++}`} />);
    }
    const line = lines[lineIdx]!;
    if (line.length === 0) continue;

    const re = /((?:https?:\/\/|www\.)[^\s<]+[^\s<.,;:!?)\]])/gi;
    let last = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(line)) !== null) {
      if (match.index > last) {
        nodes.push(line.slice(last, match.index));
      }
      const raw = match[1]!;
      const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
      nodes.push(
        <a
          key={`l-${key++}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {raw}
        </a>,
      );
      last = match.index + raw.length;
    }
    if (last < line.length) nodes.push(line.slice(last));
    else if (last === 0) nodes.push(line);
  }

  if (nodes.length === 0) nodes.push(text);
  return nodes;
}

type LayoutRow = {
  key: string;
  cueIndex: number;
  visual: string | null;
  visualSpan: number;
  audio: string | null;
  audioSpan: number;
  source: string | null;
  sourceSpan: number;
  cueId: Id | null;
};

function layoutSourceRows(
  cues: Cue[],
  columnId: Id | null,
  primaryColumnId: Id | null,
  opts?: { audioOnly?: boolean; keepCueIds?: ReadonlySet<Id> },
): LayoutRow[] {
  if (opts?.audioOnly) {
    const rows: LayoutRow[] = [];
    for (let i = 0; i < cues.length; i++) {
      const cue = cues[i]!;
      if (
        isEmptyForAudioOnlyPrint(cue) &&
        !opts.keepCueIds?.has(cue.id)
      ) {
        continue;
      }
      rows.push({
        key: cue.id,
        cueIndex: i,
        visual: null,
        visualSpan: 0,
        audio: cue.audio.content,
        audioSpan: 1,
        source: getCueNote(cue, columnId, primaryColumnId),
        sourceSpan: 1,
        cueId: cue.id,
      });
    }
    return rows;
  }

  const spans = layoutDualColumnSpans(cues);
  const rows: LayoutRow[] = [];

  for (let i = 0; i < cues.length; i++) {
    const cue = cues[i]!;
    const visualSpan = spans[i]?.visualSpan ?? 1;
    const audioSpan = spans[i]?.audioSpan ?? 1;

    rows.push({
      key: cue.id,
      cueIndex: i,
      visual: visualSpan > 0 ? cue.visual.content : null,
      visualSpan,
      audio: audioSpan > 0 ? cue.audio.content : null,
      audioSpan,
      source: getCueNote(cue, columnId, primaryColumnId),
      sourceSpan: 1,
      cueId: cue.id,
    });
  }
  return rows;
}

function NoteColumnTitle({
  column,
  onCommit,
}: {
  column: NoteColumnDef;
  onCommit: (title: string) => void;
}) {
  return (
    <InlineEditable
      as="span"
      fill
      className="sm-note-col-heading"
      value={column.title}
      placeholder="Noter"
      title="Klik for at omdøbe kolonnen"
      onChange={onCommit}
    />
  );
}

function Paras({ text }: { text: string }) {
  return (
    <div
      className="sm-rich-paras"
      dangerouslySetInnerHTML={{ __html: sanitizeCueHtml(text) }}
    />
  );
}

/** Notes: rich HTML from the editor, or auto-linked plain text from older docs. */
export function NoteRichText({ text }: { text: string }) {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (looksLikeCueHtml(trimmed)) {
    return <Paras text={trimmed} />;
  }
  return (
    <div className="sm-sources-note-display">{linkifyText(text)}</div>
  );
}

type SourceCol = ScriptColumn | "note";
type SourceCell = { cueId: Id; column: SourceCol };

function buildSourceNavOrder(
  rows: LayoutRow[],
  opts: { includeScript: boolean; audioOnly: boolean; audioFirst: boolean },
): SourceCell[] {
  const order: SourceCell[] = [];
  for (const row of rows) {
    if (!row.cueId) continue;
    if (opts.includeScript) {
      if (opts.audioOnly) {
        if (row.audio !== null) {
          order.push({ cueId: row.cueId, column: "audio" });
        }
      } else if (opts.audioFirst) {
        if (row.audio !== null) {
          order.push({ cueId: row.cueId, column: "audio" });
        }
        if (row.visual !== null) {
          order.push({ cueId: row.cueId, column: "visual" });
        }
      } else {
        if (row.visual !== null) {
          order.push({ cueId: row.cueId, column: "visual" });
        }
        if (row.audio !== null) {
          order.push({ cueId: row.cueId, column: "audio" });
        }
      }
    }
    order.push({ cueId: row.cueId, column: "note" });
  }
  return order;
}

function nextSourceCell(
  order: SourceCell[],
  from: SourceCell,
  dir: CellNavDir,
): SourceCell | null {
  const idx = order.findIndex(
    (c) => c.cueId === from.cueId && c.column === from.column,
  );
  if (idx < 0) return null;
  if (dir === "left" || dir === "right") {
    return order[idx + (dir === "right" ? 1 : -1)] ?? null;
  }
  const step = dir === "down" ? 1 : -1;
  for (let i = idx + step; i >= 0 && i < order.length; i += step) {
    if (order[i]!.column === from.column) return order[i]!;
  }
  return null;
}

export function SourcesView({
  scene,
  cues,
  readOnly,
  productionTitle,
  seasonTitle,
  episodeTitle,
  defaultSceneHeaderBg,
  showColumnRule = true,
  rowRuleMode = "custom",
  rowRuleHitInGutter = true,
  onTogglePrintBorderBelow,
  columnOrder = "visual-audio",
  noteColumns,
  activeNoteColumnId,
  onSelectNoteColumn,
  onRenameNoteColumn,
  onAddNoteColumn,
  onRemoveNoteColumn,
  onCueNoteChange,
  onSceneNoteChange,
  onCueChange,
  onNewPair,
  newRowOnDoubleReturn = true,
  onCueFocus,
  onCueBlur,
  onActiveEditorChange,
  contentResetKey,
  onSceneMetaChange,
  showHours = true,
  showFrames = false,
  fps = 25,
  clickSelectsCell = false,
  onSceneHeaderContextMenu,
  onCopyNoteColumnLink,
  focusNoteCueId = null,
  focusNoteSelectQuery,
  focusNoteSelectOccurrence = 0,
  focusNoteNonce = 0,
  onFocusNoteCueHandled,
}: SourcesViewProps) {
  const primaryId = noteColumns[0]?.id ?? null;
  const activeId = activeNoteColumnId ?? primaryId;
  const activeCol =
    noteColumns.find((c) => c.id === activeId) ?? noteColumns[0] ?? null;
  const columnTitle = activeCol?.title?.trim() || "Noter";
  const audioOnly = isAudioOnlyColumnOrder(columnOrder);
  const audioFirst = columnOrder === "audio-visual";
  const [scriptUnlocked, setScriptUnlocked] = useState(false);
  const canEditScript = Boolean(scriptUnlocked && onCueChange && !readOnly);
  const [focusCell, setFocusCell] = useState<{
    cueId: Id;
    column: SourceCol;
    nonce: number;
    edge: "start" | "end";
    selectQuery?: string;
    selectOccurrence?: number;
  } | null>(null);
  const [audioOnlyHoldCueId, setAudioOnlyHoldCueId] = useState<Id | null>(null);
  const keepCueIds = useMemo(() => {
    if (!audioOnly) return undefined;
    const ids = new Set<Id>();
    if (focusCell?.cueId) ids.add(focusCell.cueId);
    if (focusNoteCueId) ids.add(focusNoteCueId);
    if (audioOnlyHoldCueId) ids.add(audioOnlyHoldCueId);
    return ids;
  }, [audioOnly, audioOnlyHoldCueId, focusCell?.cueId, focusNoteCueId]);
  const rows = useMemo(
    () =>
      layoutSourceRows(cues, activeId, primaryId, {
        audioOnly,
        keepCueIds,
      }),
    [activeId, audioOnly, cues, keepCueIds, primaryId],
  );

  useEffect(() => {
    if (!audioOnly) setAudioOnlyHoldCueId(null);
  }, [audioOnly]);

  const navOrder = useMemo(
    () =>
      buildSourceNavOrder(rows, {
        includeScript: canEditScript,
        audioOnly,
        audioFirst,
      }),
    [rows, canEditScript, audioOnly, audioFirst],
  );

  const handleNavigate = useCallback(
    (from: SourceCell, dir: CellNavDir) => {
      const next = nextSourceCell(navOrder, from, dir);
      if (!next) return;
      setFocusCell((prev) => ({
        ...next,
        nonce: (prev?.nonce ?? 0) + 1,
        edge: dir === "left" || dir === "up" ? "end" : "start",
      }));
    },
    [navOrder],
  );

  const handleNewPair = useCallback(
    (cueId: Id, column: ScriptColumn, flushed: string) => {
      if (!canEditScript || !onNewPair) return;
      const newId = onNewPair(cueId, column, flushed, "after");
      if (!newId) return;
      if (audioOnly) setAudioOnlyHoldCueId(newId);
      setFocusCell((prev) => ({
        cueId: newId,
        column,
        nonce: (prev?.nonce ?? 0) + 1,
        edge: "start",
      }));
    },
    [audioOnly, canEditScript, onNewPair],
  );

  const cellFocus = (cueId: Id, column: SourceCol) =>
    focusCell?.cueId === cueId && focusCell.column === column
      ? {
          nonce: focusCell.nonce,
          edge: focusCell.edge,
          selectQuery: focusCell.selectQuery,
          selectOccurrence: focusCell.selectOccurrence,
        }
      : {
          nonce: 0,
          edge: "end" as const,
          selectQuery: undefined as string | undefined,
          selectOccurrence: 0,
        };

  useEffect(() => {
    setFocusCell(null);
  }, [activeId]);

  useEffect(() => {
    if (!focusNoteCueId) return;
    setFocusCell((prev) => ({
      cueId: focusNoteCueId,
      column: "note",
      nonce: (prev?.nonce ?? 0) + 1,
      edge: "start",
      selectQuery: focusNoteSelectQuery,
      selectOccurrence: focusNoteSelectOccurrence,
    }));
    onFocusNoteCueHandled?.();
  }, [
    focusNoteCueId,
    focusNoteSelectQuery,
    focusNoteSelectOccurrence,
    focusNoteNonce,
    onFocusNoteCueHandled,
  ]);

  return (
    <div className="sm-paper sm-paper-a4-landscape sm-view-sources">
      {!readOnly ? (
        <div className="sm-paper-tools sm-no-print">
          <button
            type="button"
            className={scriptUnlocked ? "is-active" : undefined}
            aria-pressed={scriptUnlocked}
            title={
              scriptUnlocked
                ? "Lås manusfelterne, så de kun kan læses"
                : "Lås op for at redigere billed- og lydside her"
            }
            onClick={() => {
              setScriptUnlocked((on) => {
                if (on) onActiveEditorChange?.(null);
                return !on;
              });
            }}
          >
            {scriptUnlocked ? "Lås manus" : "Lås op"}
          </button>
        </div>
      ) : null}
      <DocumentHead
        productionTitle={productionTitle}
        seasonTitle={seasonTitle}
        episodeTitle={episodeTitle}
        view="sources"
      />
      <div
        className="sm-script-scene-head"
        style={{
          backgroundColor: sceneHeaderBg(
            scene.headerBackground,
            defaultSceneHeaderBg,
          ),
        }}
        onContextMenu={
          onSceneHeaderContextMenu
            ? (e) => {
                e.preventDefault();
                onSceneHeaderContextMenu(e);
              }
            : undefined
        }
        title={
          onSceneHeaderContextMenu
            ? "Højreklik for baggrundsfarve, dupliker, flyt, opdel eller slet"
            : undefined
        }
      >
        <div className="sm-script-scene-head-row">
          <div className="sm-script-scene-head-main">
            <SceneHeadingLine
              sceneNumber={scene.number}
              heading={scene.heading}
              headingKind={scene.headingKind}
              headingLocation={scene.headingLocation}
              headingTime={scene.headingTime}
              headingNote={scene.headingNote}
              readOnly={readOnly || !onSceneMetaChange}
              onChange={(patch) => onSceneMetaChange?.(patch)}
              onNumberChange={
                onSceneMetaChange
                  ? (number) => onSceneMetaChange({ number })
                  : undefined
              }
            />
            <InlineEditable
              as="strong"
              fill
              className="sm-script-scene-title"
              value={scene.title}
              onChange={(title) => onSceneMetaChange?.({ title })}
              readOnly={readOnly || !onSceneMetaChange}
              placeholder="Unavngiven scene"
            />
          </div>
          <DurationField
            className="sm-script-scene-duration"
            valueSeconds={scene.durationSeconds}
            onChange={(durationSeconds) =>
              onSceneMetaChange?.({ durationSeconds })
            }
            readOnly={readOnly || !onSceneMetaChange}
            showHours={showHours}
            showFrames={showFrames}
            fps={fps}
            ariaLabel={`Varighed for scene ${scene.number}`}
          />
        </div>
      </div>

      <div className="sm-note-cols" role="tablist" aria-label="Note-kolonner">
        <span className="sm-note-cols-label">Kolonne</span>
        {noteColumns.map((col) => (
          <button
            key={col.id}
            type="button"
            role="tab"
            aria-selected={col.id === activeId}
            className={`sm-note-col-chip${
              col.id === activeId ? " is-active" : ""
            }`}
            onClick={() => onSelectNoteColumn(col.id)}
            onContextMenu={
              onCopyNoteColumnLink
                ? (e) => {
                    e.preventDefault();
                    onCopyNoteColumnLink(col.id);
                  }
                : undefined
            }
            title={
              onCopyNoteColumnLink
                ? "Højreklik for at kopiere link til kolonnen"
                : undefined
            }
          >
            {col.title.trim() || "Noter"}
          </button>
        ))}
        {!readOnly ? (
          <>
            <button
              type="button"
              className="sm-note-col-add"
              onClick={onAddNoteColumn}
            >
              + Ny kolonne
            </button>
            {activeId && noteColumns.length > 1 ? (
              <button
                type="button"
                className="sm-note-col-remove"
                onClick={() => onRemoveNoteColumn(activeId)}
              >
                Fjern kolonne
              </button>
            ) : null}
          </>
        ) : null}
      </div>

      {activeId ? (
        <div className="sm-sources-scene-note">
          <div className="sm-sources-scene-note-label">Note for hele scenen</div>
          <div className="sm-sources-note sm-sources-scene-note-body">
            {readOnly || !onSceneNoteChange ? (
              !isCueContentEmpty(getSceneNote(scene, activeId)) ? (
                <NoteRichText text={getSceneNote(scene, activeId)} />
              ) : (
                <div className="sm-sources-note-display is-empty">—</div>
              )
            ) : (
              <CueHtmlEditor
                key={`scene-note-${scene.id}-${activeId}-${contentResetKey ?? ""}`}
                content={getSceneNote(scene, activeId)}
                clickSelectsCell={clickSelectsCell}
                onChange={(html) => onSceneNoteChange(activeId, html)}
                onEditor={onActiveEditorChange}
              />
            )}
          </div>
        </div>
      ) : null}

      <table
        className={`sm-sources-table${
          showColumnRule && !audioOnly ? " sm-col-rule" : ""
        }${audioOnly ? " sm-audio-only" : audioFirst ? " sm-audio-left" : " sm-visual-left"}${
          rowRuleHitInGutter && !audioOnly && onTogglePrintBorderBelow
            ? " sm-row-rule-gutter-hit"
            : ""
        }`}
      >
        <thead>
          <tr>
            {audioOnly ? (
              <th scope="col" className="sm-sources-audio">
                Lydside
              </th>
            ) : audioFirst ? (
              <>
                <th scope="col" className="sm-sources-audio">
                  Lydside
                </th>
                <th scope="col" className="sm-sources-visual">
                  Billedside
                </th>
              </>
            ) : (
              <>
                <th scope="col" className="sm-sources-visual">
                  Billedside
                </th>
                <th scope="col" className="sm-sources-audio">
                  Lydside
                </th>
              </>
            )}
            <th scope="col" className="sm-sources-note">
              {readOnly || !activeCol ? (
                columnTitle
              ) : (
                <NoteColumnTitle
                  column={activeCol}
                  onCommit={(title) =>
                    onRenameNoteColumn(activeCol.id, title)
                  }
                />
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const cue = cues[row.cueIndex];
            if (!cue) return null;
            const visualSpan = row.visualSpan;
            const audioSpan = row.audioSpan;
            const ruleBelow = cueShowsRowRule(cue, rowRuleMode);
            const showRuleHit =
              !readOnly &&
              rowRuleMode === "custom" &&
              Boolean(onTogglePrintBorderBelow);
            const ruleOnVisual = showRuleHit && visualSpan === 1;
            const ruleOnAudio = showRuleHit && audioSpan === 1;
            const ruleOnNote = Boolean(
              showRuleHit && row.cueId && row.sourceSpan === 1,
            );

            let visualRuleEnd = false;
            if (visualSpan > 1) {
              visualRuleEnd = cueShowsRowRule(
                cues[row.cueIndex + visualSpan - 1],
                rowRuleMode,
              );
            } else if (visualSpan === 1) {
              visualRuleEnd = cueShowsRowRule(cue, rowRuleMode);
            }

            let audioRuleEnd = false;
            if (audioSpan > 1) {
              audioRuleEnd = cueShowsRowRule(
                cues[row.cueIndex + audioSpan - 1],
                rowRuleMode,
              );
            } else if (audioSpan === 1) {
              audioRuleEnd = cueShowsRowRule(cue, rowRuleMode);
            }

            const hit = (active: boolean) =>
              row.cueId && onTogglePrintBorderBelow ? (
                <RowRuleHit
                  active={active}
                  onToggle={() => onTogglePrintBorderBelow(row.cueId!)}
                />
              ) : null;

            const visualTd =
              row.visual !== null ? (
                <td
                  key="visual"
                  rowSpan={visualSpan}
                  className={`sm-sources-visual${
                    visualSpan === 1 ? " sm-rule-edge" : ""
                  }${visualSpan > 1 ? " sm-is-rowspan" : ""}${
                    visualRuleEnd && visualSpan > 1 ? " sm-span-rule-end" : ""
                  }`}
                >
                  {canEditScript && row.cueId ? (
                    <CueHtmlEditor
                      key={`${row.cueId}-visual-${contentResetKey ?? ""}`}
                      content={row.visual}
                      clickSelectsCell={clickSelectsCell}
                      focusNonce={cellFocus(row.cueId, "visual").nonce}
                      focusEdge={cellFocus(row.cueId, "visual").edge}
                      newRowOnDoubleReturn={newRowOnDoubleReturn}
                      onChange={(html) =>
                        onCueChange?.(row.cueId!, "visual", html)
                      }
                      onFocus={() => onCueFocus?.(row.cueId!, "visual")}
                      onBlur={() => onCueBlur?.(row.cueId!, "visual")}
                      onEditor={onActiveEditorChange}
                      onNewPair={(flushed) =>
                        handleNewPair(row.cueId!, "visual", flushed)
                      }
                      onNavigate={(dir) =>
                        handleNavigate(
                          { cueId: row.cueId!, column: "visual" },
                          dir,
                        )
                      }
                    />
                  ) : (
                    <Paras text={row.visual} />
                  )}
                  {ruleOnVisual ? hit(ruleBelow) : null}
                </td>
              ) : null;
            const audioTd =
              row.audio !== null ? (
                <td
                  key="audio"
                  rowSpan={audioSpan}
                  className={`sm-sources-audio${
                    audioSpan === 1 ? " sm-rule-edge" : ""
                  }${audioSpan > 1 ? " sm-is-rowspan" : ""}${
                    audioRuleEnd && audioSpan > 1 ? " sm-span-rule-end" : ""
                  }`}
                >
                  {canEditScript && row.cueId ? (
                    <CueHtmlEditor
                      key={`${row.cueId}-audio-${contentResetKey ?? ""}`}
                      content={row.audio}
                      clickSelectsCell={clickSelectsCell}
                      focusNonce={cellFocus(row.cueId, "audio").nonce}
                      focusEdge={cellFocus(row.cueId, "audio").edge}
                      newRowOnDoubleReturn={newRowOnDoubleReturn}
                      onChange={(html) =>
                        onCueChange?.(row.cueId!, "audio", html)
                      }
                      onFocus={() => {
                        if (audioOnly) setAudioOnlyHoldCueId(row.cueId!);
                        onCueFocus?.(row.cueId!, "audio");
                      }}
                      onBlur={() => {
                        setAudioOnlyHoldCueId((cur) =>
                          cur === row.cueId ? null : cur,
                        );
                        onCueBlur?.(row.cueId!, "audio");
                      }}
                      onEditor={onActiveEditorChange}
                      onNewPair={(flushed) =>
                        handleNewPair(row.cueId!, "audio", flushed)
                      }
                      onNavigate={(dir) =>
                        handleNavigate(
                          { cueId: row.cueId!, column: "audio" },
                          dir,
                        )
                      }
                    />
                  ) : (
                    <Paras text={row.audio} />
                  )}
                  {ruleOnAudio ? hit(ruleBelow) : null}
                </td>
              ) : null;
            return (
              <Fragment key={row.key}>
                <tr className={`sm-cue-row${ruleBelow ? " sm-rule-below" : ""}`}>
                  {audioOnly ? (
                    audioTd
                  ) : audioFirst ? (
                    <>
                      {audioTd}
                      {visualTd}
                    </>
                  ) : (
                    <>
                      {visualTd}
                      {audioTd}
                    </>
                  )}
                  {row.cueId && activeId ? (
                    <td
                      rowSpan={row.sourceSpan}
                      className={`sm-sources-note${
                        row.sourceSpan === 1 ? " sm-rule-edge" : ""
                      }`}
                    >
                      {readOnly ? (
                        row.source?.trim() ? (
                          <NoteRichText text={row.source} />
                        ) : (
                          <div className="sm-sources-note-display is-empty">
                            —
                          </div>
                        )
                      ) : (
                        <CueHtmlEditor
                          key={`${row.cueId}-note-${activeId}-${contentResetKey ?? ""}`}
                          content={row.source ?? ""}
                          clickSelectsCell={clickSelectsCell}
                          focusNonce={cellFocus(row.cueId, "note").nonce}
                          focusEdge={cellFocus(row.cueId, "note").edge}
                          selectQuery={cellFocus(row.cueId, "note").selectQuery}
                          selectOccurrence={
                            cellFocus(row.cueId, "note").selectOccurrence
                          }
                          onChange={(html) =>
                            activeId
                              ? onCueNoteChange(row.cueId!, activeId, html)
                              : undefined
                          }
                          onFocus={() => {
                            if (audioOnly) setAudioOnlyHoldCueId(row.cueId!);
                          }}
                          onBlur={() => {
                            setAudioOnlyHoldCueId((cur) =>
                              cur === row.cueId ? null : cur,
                            );
                          }}
                          onEditor={onActiveEditorChange}
                          onNavigate={(dir) =>
                            handleNavigate(
                              { cueId: row.cueId!, column: "note" },
                              dir,
                            )
                          }
                        />
                      )}
                      {ruleOnNote ? hit(ruleBelow) : null}
                    </td>
                  ) : null}
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RowRuleHit({
  active,
  onToggle,
}: {
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className="sm-row-rule-hit"
      title={
        active
          ? "Skjul vandret streg på udskrift"
          : "Vis vandret streg på udskrift"
      }
      aria-pressed={active}
      aria-label="Skift vandret streg under denne række"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
    />
  );
}
